import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  ThinTextField,
  AddButton,
  ThinDropdownWithSearch,
  BackButton,
  ThinDropdown,
} from "../../../components/common/GeneralComponent";
import { fetchData } from "../../../utils/functions";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Loader from "../../../components/partials/Loader";

const EditVillage = () => {
  const { id } = useParams();
  const [loader, setLoader] = useState(true);
  const [vid, setVid] = useState("");
  const [districts, setDistricts] = useState([]);
  const [tehsils, setTehsils] = useState([]);
  const [dehs, setDehs] = useState([]);
  const [filteredDistricts, setFilteredDistricts] = useState([]);
  const [filteredTehsils, setFilteredTehsils] = useState([]);
  const [filteredDehs, setFilteredDehs] = useState([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchDataForEdit = async () => {
      try {
        const villageRes = await fetchData(
          "all-villages",
          "GET",
          null,
          {},
          { village_id: id },
          navigate,
          "/villages",
          dispatch
        );
        const villageData = villageRes?.data?.[0];

        if (villageData) {
          formik.setValues({
            village_id: id,
            village_name: villageData.village_name,
            district_id: villageData.district_id,
            tehsil_id: villageData.tehsil_id,
            deh_id: villageData.deh_id,
            status: villageData.status,
            type: villageData.type || "Rural",
          });
          setVid(villageData.vid || "");

          if (villageData.district_id) {
            const disRes = await fetchData(
              "districts",
              "GET",
              null,
              {},
              {},
              navigate,
              "/districts",
              dispatch
            );
            const allDists = disRes?.data || [];
            setDistricts(allDists);
            setFilteredDistricts(allDists.filter((d) => d.status === 1));
          }
          if (villageData.district_id) {
            const tehRes = await fetchData(
              "tehsils",
              "GET",
              null,
              {},
              { district_id: villageData.district_id },
              navigate,
              "/tehsils",
              dispatch
            );
            const allTehs = tehRes?.data || [];
            setTehsils(allTehs);
            setFilteredTehsils(allTehs.filter((t) => t.status === 1));
          }
          if (villageData.tehsil_id) {
            const dehRes = await fetchData(
              "dehs",
              "GET",
              null,
              {},
              { tehsil_id: villageData.tehsil_id },
              navigate,
              "/dehs",
              dispatch
            );
            const allDehsData = dehRes?.data || [];
            setDehs(allDehsData);
            setFilteredDehs(allDehsData.filter((d) => d.status === 1));
          }
        } else {
          toast.error("Village not found");
          navigate("/villages");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load data");
        navigate("/villages");
      } finally {
        setLoader(false);
      }
    };

    if (id) {
      fetchDataForEdit();
    }
  }, [id, navigate, dispatch]);

const validationSchema = Yup.object({
  village_name: Yup.string()
    .required("Village Name is required")
    .max(100, "Village name must be 100 characters or less"),
  district_id: Yup.string().required("District is required"),
  tehsil_id: Yup.string().required("Tehsil is required"),
  deh_id: Yup.string().required("Deh is required"),
  status: Yup.number().required("Status is required"),
  type: Yup.string().required("Type is required"),
});

  const formik = useFormik({
    initialValues: {
      village_id: id,
      village_name: "",
      district_id: "",
      tehsil_id: "",
      deh_id: "",
      status: 1,
      type: "Rural",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        // ✅ Check for duplicate village name before saving
        const res = await fetchData(
          "all-villages",
          "GET",
          null,
          {},
          { village_name: values.village_name },
          navigate,
          "/villages",
          dispatch
        );

        const villages = res?.data || [];
        const duplicate = villages.some(
          (v) =>
            v.village_name?.toLowerCase() ===
              values.village_name.toLowerCase() &&
            String(v.village_id) !== String(id)
        );

        if (duplicate) {
          toast.error("Village name already exists");
          setIsSubmitting(false);
          return;
        }

        // ✅ Proceed if no duplicate
        await fetchData(
          "villages",
          "PUT",
          values,
          {},
          {},
          navigate,
          "/villages",
          dispatch
        );
        toast.success("Village updated successfully");
        navigate("/villages");
      } catch (error) {
        toast.error(error.message || "Failed to update Village");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleDistrictChange = async (selectedValue) => {
    formik.setValues((prev) => ({
      ...prev,
      district_id: selectedValue,
      tehsil_id: "",
      deh_id: "",
    }));

    if (selectedValue) {
      formik.setFieldError("district_id", undefined);

      try {
        setLoader(true);
        const res = await fetchData(
          "tehsils",
          "GET",
          null,
          {},
          { district_id: selectedValue },
          navigate,
          "/tehsils",
          dispatch
        );
        const allTehs = res?.data || [];
        setTehsils(allTehs);
        const activeTehsils = allTehs.filter((t) => t.status === 1);
        setFilteredTehsils(activeTehsils);
      } catch (error) {
        //console.error("Error fetching tehsils:", error);
        //toast.error("Failed to load tehsils");
      } finally {
        setLoader(false);
      }
    } else {
      setFilteredTehsils([]);
      setFilteredDehs([]);
    }
  };

  const handleTehsilChange = async (selectedValue) => {
    formik.setValues((prev) => ({
      ...prev,
      tehsil_id: selectedValue,
      deh_id: "",
    }));

    if (selectedValue) {
      formik.setFieldError("tehsil_id", undefined);

      try {
        setLoader(true);
        const res = await fetchData(
          "dehs",
          "GET",
          null,
          {},
          { tehsil_id: selectedValue },
          navigate,
          "/dehs",
          dispatch
        );
        const allDehsData = res?.data || [];
        setDehs(allDehsData);
        const activeDehs = allDehsData.filter((d) => d.status === 1);
        setFilteredDehs(activeDehs);
      } catch (error) {
        setFilteredDehs([]);
        //console.error("Error fetching Dehs:", error);
        //toast.error("Failed to load Dehs");
      } finally {
        setLoader(false);
      }
    } else {
      setFilteredDehs([]);
    }
  };

  if (loader) return <Loader />;

  return (
    <div className="w-full my-4 mx-auto">
      <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
        <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
          Edit Village
        </h1>
        <BackButton text="Back" to="/villages" />
      </div>
      <div className="bg-white rounded-lg shadow w-full lg:mb-[30px] mt-4 mb-[7rem]">
        <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b mb-4">
          <h1 className="lg:text-[0.875rem] text-[13px] tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
            Edit Village
          </h1>
        </div>
        <form onSubmit={formik.handleSubmit} className="p-4">
          <div className="grid grid-cols-2 gap-4 p-4 my-4">
            <div>
              <ThinDropdownWithSearch
                label="District"
                options={filteredDistricts.map((d) => ({
                  value: d.district_id || d.District_Id,
                  label: d.district_name || d.District_Name,
                }))}
                value={formik.values.district_id}
                onChange={handleDistrictChange}
                onBlur={formik.handleBlur}
                error={formik.touched.district_id && formik.errors.district_id}
                helperText={
                  formik.touched.district_id && formik.errors.district_id
                }
              />
            </div>

            <div>
              <ThinDropdownWithSearch
                label="Tehsil"
                options={filteredTehsils.map((t) => ({
                  value: t.tehsil_id || t.Tehsil_Id,
                  label: t.tehsil_name || t.Tehsil_Name,
                }))}
                value={formik.values.tehsil_id}
                onChange={handleTehsilChange}
                onBlur={formik.handleBlur}
                disabled={!formik.values.district_id}
                error={formik.touched.tehsil_id && formik.errors.tehsil_id}
                helperText={
                  formik.touched.tehsil_id && formik.errors.tehsil_id
                }
              />
            </div>

            <div>
              <ThinDropdownWithSearch
                label="Deh"
                options={filteredDehs.map((d) => ({
                  value: d.deh_id,
                  label: d.deh_name,
                }))}
                value={formik.values.deh_id}
                onChange={(val) => formik.setFieldValue("deh_id", val)}
                onBlur={formik.handleBlur}
                disabled={!formik.values.tehsil_id}
                error={formik.touched.deh_id && formik.errors.deh_id}
                helperText={
                  formik.touched.deh_id && formik.errors.deh_id
                }
              />
            </div>

            <ThinTextField
              label="Village Name"
              name="village_name"
              value={formik.values.village_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.village_name && formik.errors.village_name}
              helperText={
                formik.touched.village_name && formik.errors.village_name
              }
            />

            <ThinDropdown
              label="Status"
              id="status-dropdown"
              options={[
                { value: 1, label: "Active" },
                { value: 0, label: "In-Active" },
              ]}
              value={formik.values.status}
              onChange={(e) => formik.setFieldValue("status", e.target.value)}
              error={formik.touched.status && formik.errors.status}
            />

            <ThinDropdownWithSearch
              label="Type"
              options={[
                { value: "Rural", label: "Rural" },
                { value: "Urban", label: "Urban" },
              ]}
              value={formik.values.type}
              onChange={(val) => formik.setFieldValue("type", val)}
              error={formik.touched.type && formik.errors.type}
              helperText={formik.touched.type && formik.errors.type}
            />

            <ThinTextField
              label="VID"
              name="vid"
              value={vid}
              readOnly
              isRequired={false}
              onChange={() => {}}
            />
          </div>

          <div className="p-4">
            <AddButton
              text="Submit"
              type="Submit"
              isLoading={isSubmitting}
              disabled={isSubmitting}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditVillage;
